// BL-1634 (ADR 058 — Customer Identity) — the pure derivation behind
// `<CustomerIdentity>`: which facts appear, and in what order. Re-ordered
// company-first by BL-1818 (Dan Saper, 2026-09-18) and moved here from
// admin-frontend so server-built one-line copy reads the same string the
// component renders.
//
// The component renders whatever this returns, so every rule about WHICH name
// leads lives here, once:
//
//   1. Line one is `resolveCustomerDisplayName` — never a per-surface reorder of
//      its rungs (company → person → account label → email). A Business with a
//      contact person leads with the company; one with no contact person leads
//      with the company alone; the sentinel would be a lie there.
//   2. The second fact is the OTHER name — the person under a company. Both
//      names always, so nobody has to pick which half of a Business to show.
//   3. The sentinel (`NO_NAME_ON_FILE`) replaces line one only when the resolver
//      returned `null` OR fell through to the customer's own email address —
//      the STRING comparison from BidderIdentityCell (BUG-1389), trim- and
//      case-insensitive, not a "looks like an email" test: a name that is a
//      DIFFERENT address from the login is two facts, neither a duplicate. The
//      same equality keeps a company that IS the login address (BUG-1327) off
//      both lines.
//   4. A fact never prints twice: the second fact is dropped when it IS line
//      one; the email is shown once, as the second fact of a placeholder row.
//      With no address, `emailMissing` tells the block forms to show
//      `NO_EMAIL_ON_FILE` there (BL-2049); one-line copy stays the bare sentinel.
//   5. The avatar derives from line one — the company's initials under a
//      company — and from the address for a nameless buyer, never "NN" spelled
//      out of the placeholder.
//
// Pure and client-safe (ADR 010 — Isomorphic Services): the component runs it
// on every render, and server copy (an audit chip, a rejection sentence) runs
// it to write the same one-line `label` / `inline` string.

import {
    NO_NAME_ON_FILE,
    companyUnlessOwnEmail,
    resolveCustomerDisplayName,
    type CustomerDisplayNameInput,
} from './resolvePreferredPhone';

/**
 * The identity record the derivation reads: the resolver's rungs plus the two
 * facts the forms add. Declared structurally here, not imported, because this
 * module sits below `core-admin` — admin's `CustomerIdentityDto` satisfies it
 * as-is (ADR 010 — Isomorphic Services: typed on the minimum fields it needs).
 */
export interface CustomerIdentityLinesInput extends CustomerDisplayNameInput {
    /** `customers.account_type` — `Business` / `Individual`. */
    accountType?: string | null;
    /** The bidder's paddle in the auction the surface belongs to. */
    paddleNumber?: string | null;
}

/** What the five forms render, already de-duplicated and ordered. */
export interface CustomerIdentityLines {
    /** Line one — the resolver's answer (the company when there is one), or the sentinel. */
    primary: string;
    /** True when `primary` is the sentinel (no name, or the name was the email). */
    isPlaceholder: boolean;
    /** The other name — the person under a company — unless it is already line one. */
    secondary: string | null;
    /** `customers.email`, trimmed. */
    email: string | null;
    /**
     * True when the row is the sentinel AND has no address to show beneath it —
     * the block forms render `NO_EMAIL_ON_FILE` there (BL-2049, ADR 058 § 4);
     * `label` / `inline` stay the bare sentinel. Never true under a real name.
     */
    emailMissing: boolean;
    /** `#219`, or `null` off an auction. */
    paddle: string | null;
    /** "Business account" / "Individual account" — the `full` form's middle line. */
    accountTypeLabel: string | null;
    /** What the avatar's initials derive from — line one, else the address. */
    initialsSource: string;
    /**
     * Both names on one line, no paddle: `Company (Person)`. The `compact` form's
     * text, and the string an aria-label, a confirm sentence or a picker's
     * closed field uses. No empty parenthetical, ever.
     */
    label: string;
    /** The prose form: `Company (Person, #Paddle)` — no empty parenthetical, ever. */
    inline: string;
}


const nonBlank = (value: string | null | undefined): string | null => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
};

/**
 * True when the display name and the email line would print the SAME address.
 *
 * BUG-1389: a self-registered buyer has no name on file, so the resolver falls
 * through to their email address and a two-line identity would print it twice.
 * Intentionally an equality test, not "does the name look like an email": a
 * bidder whose name is one address and whose login is another still has two
 * distinct facts on the row. Exported for the Bidders roster, which prints the
 * row's own address beneath the identity and must skip it where the sentinel
 * already printed that address (BL-1990) — one implementation, not two. The
 * derivation also uses it to keep a company that IS the login address off the
 * row.
 *
 * @param name - the resolved display name.
 * @param email - `customers.email`, or null.
 * @returns true when both would show the same address.
 */
export const nameIsDuplicateOfEmail = (name: string, email: string | null): boolean => {
    const address = email?.trim().toLowerCase() ?? '';
    return address !== '' && name.trim().toLowerCase() === address;
};

/**
 * Derives the identity lines for one customer record.
 *
 * @param record - the slim identity record (every field a `customers` column).
 * @returns the ordered, de-duplicated facts each form renders.
 * @sideEffect None — pure function.
 */
export function deriveCustomerIdentityLines(record: CustomerIdentityLinesInput): CustomerIdentityLines {
    const email = nonBlank(record.customerEmail);
    const resolved = resolveCustomerDisplayName(record);
    const isPlaceholder = resolved == null || nameIsDuplicateOfEmail(resolved, email);
    const primary = isPlaceholder ? NO_NAME_ON_FILE : resolved;

    // The company is a fact only when it is not the customer's own address —
    // the resolver's own rung guard, so the two can never disagree.
    const company = companyUnlessOwnEmail(record.companyName, record.customerEmail);
    const person = nonBlank(record.fullName);
    const secondary = isPlaceholder
        ? null
        : [company, person].find((name): name is string => name != null && name !== primary) ?? null;

    const paddleRaw = nonBlank(record.paddleNumber);
    const paddle = paddleRaw ? `#${paddleRaw}` : null;
    const accountTypeLabel = record.accountType ? `${record.accountType} account` : null;

    // In a one-line form the placeholder carries the address where a name
    // would carry the other name — the one fact that identifies the row. The
    // one-line copy (audit chips, sentences) keeps the bare sentinel when there
    // is no address; only the block forms add the amber stand-in (BL-2049).
    const emailMissing = isPlaceholder && email == null;
    const second = isPlaceholder ? email : secondary;
    const label = second ? `${primary} (${second})` : primary;
    const parenthetical = [second, paddle].filter((part): part is string => part != null);
    const inline = parenthetical.length > 0 ? `${primary} (${parenthetical.join(', ')})` : primary;

    return {
        primary,
        isPlaceholder,
        secondary,
        email,
        emailMissing,
        paddle,
        accountTypeLabel,
        initialsSource: isPlaceholder ? (email ?? '') : primary,
        label,
        inline,
    };
}
